/**
 * PublicHeader - Unified header for all public pages
 *
 * Features:
 * - Consistent navigation across LandingPage and all public pages
 * - Responsive hamburger menu for mobile
 * - Products dropdown on desktop with icons and effects
 * - Scroll effect for sticky header
 * - Auth modal integration
 */

import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, FileText, BookOpen, ArrowRight, User, LogOut, Settings, CreditCard, Shield, LayoutDashboard } from "lucide-react";
import logoSvg from "@/assets/logo.svg";
import { AuthModal, type AuthModalView } from "@/components/auth/AuthModal";
import { useAuth } from "@/lib/auth-context";

// Navigation structure with icons for dropdown
const NAV_LINKS = {
  products: {
    label: "Produkter",
    items: [
      {
        href: "/arsredovisning",
        label: "Årsredovisning",
        description: "Komplett K2/K3-årsredovisning",
        icon: FileText,
        color: "text-blue-600",
        bgColor: "bg-blue-50"
      },
      {
        href: "/arsredovisning/k2",
        label: "K2 Årsredovisning",
        description: "För mindre företag",
        icon: FileText,
        color: "text-sky-600",
        bgColor: "bg-sky-50"
      },
      {
        href: "/arsredovisning/k3",
        label: "K3 Årsredovisning",
        description: "Huvudregelverket",
        icon: FileText,
        color: "text-indigo-600",
        bgColor: "bg-indigo-50"
      },
      {
        href: "/aktiebok",
        label: "Digital aktiebok",
        description: "Hantera aktieägare enkelt",
        icon: BookOpen,
        color: "text-emerald-600",
        bgColor: "bg-emerald-50"
      },
    ],
  },
  main: [
    { href: "/priser", label: "Priser" },
    { href: "/arsredovisning/guider", label: "Guider" },
    { href: "/vanliga-fragor", label: "FAQ" },
  ],
};

export function PublicHeader() {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalView, setAuthModalView] = useState<AuthModalView>("login");
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const productsDropdownRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Track scroll position for header effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsProductsOpen(false);
  }, [location.pathname]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        productsDropdownRef.current &&
        !productsDropdownRef.current.contains(event.target as Node)
      ) {
        setIsProductsOpen(false);
      }
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node)
      ) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      setIsUserMenuOpen(false);
      setIsMobileMenuOpen(false);
      navigate("/");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);

  const openLogin = () => {
    setAuthModalView("login");
    setShowAuthModal(true);
    setIsMobileMenuOpen(false);
  };

  const openRegister = () => {
    setAuthModalView("register");
    setShowAuthModal(true);
    setIsMobileMenuOpen(false);
  };

  const isLandingPage = location.pathname === "/";

  return (
    <>
      <header
        className={`sticky top-0 z-50 backdrop-blur-md border-b transition-all duration-300 ${
          isScrolled
            ? "bg-white/95 border-gray-200 shadow-md"
            : "bg-white/80 border-gray-100"
        }`}
      >
        <nav aria-label="Huvudnavigation">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              {/* Logo */}
              <Link
                to="/"
                className="flex items-center flex-shrink-0"
                onClick={(e) => {
                  if (isLandingPage) {
                    e.preventDefault();
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }
                }}
              >
                <img src={logoSvg} alt="Kvanten" className="h-10 w-auto" />
              </Link>

              {/* Desktop Navigation */}
              <div className="hidden md:flex items-center gap-1">
                {/* Products Dropdown */}
                <div className="relative" ref={productsDropdownRef}>
                  <button
                    onClick={() => setIsProductsOpen(!isProductsOpen)}
                    className={`flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                      isProductsOpen
                        ? "text-blue-600 bg-blue-50"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    }`}
                    aria-expanded={isProductsOpen}
                    aria-haspopup="true"
                  >
                    {NAV_LINKS.products.label}
                    <ChevronDown
                      className={`w-4 h-4 transition-transform duration-300 ${
                        isProductsOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {/* Enhanced Dropdown Menu */}
                  <div
                    className={`absolute left-0 mt-2 w-72 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden transition-all duration-300 origin-top ${
                      isProductsOpen
                        ? "opacity-100 scale-100 translate-y-0"
                        : "opacity-0 scale-95 -translate-y-2 pointer-events-none"
                    }`}
                  >
                    {/* Gradient top border */}
                    <div className="h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-emerald-500" />

                    <div className="p-2">
                      {NAV_LINKS.products.items.map((item) => (
                        <Link
                          key={item.href}
                          to={item.href}
                          className="group flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50 transition-all duration-200"
                          onClick={() => setIsProductsOpen(false)}
                        >
                          <div className={`p-2 rounded-lg ${item.bgColor} transition-transform duration-200 group-hover:scale-110`}>
                            <item.icon className={`w-5 h-5 ${item.color}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                                {item.label}
                              </span>
                              <ArrowRight className="w-3 h-3 text-gray-400 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200" />
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {item.description}
                            </p>
                          </div>
                        </Link>
                      ))}
                    </div>

                    {/* Footer with CTA */}
                    <div className="bg-gray-50 px-4 py-3 border-t border-gray-100">
                      <Link
                        to="/priser"
                        className="text-xs font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1"
                        onClick={() => setIsProductsOpen(false)}
                      >
                        Se alla priser
                        <ArrowRight className="w-3 h-3" />
                      </Link>
                    </div>
                  </div>
                </div>

                {/* Main Links */}
                {NAV_LINKS.main.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>

              {/* Auth Buttons - Desktop */}
              <div className="hidden md:flex items-center gap-3">
                {isAuthenticated ? (
                  <>
                    {/* Dashboard button */}
                    <Link
                      to="/dashboard"
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <LayoutDashboard size={16} />
                      Dashboard
                    </Link>

                    {/* User dropdown */}
                    <div className="relative" ref={userMenuRef}>
                      <button
                        onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                        className="flex items-center gap-2 pl-1 pr-2 py-1.5 rounded-full hover:bg-gray-100 transition-colors"
                      >
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <User size={16} className="text-blue-600" />
                        </div>
                        <ChevronDown
                          size={14}
                          className={`text-gray-400 transition-transform ${isUserMenuOpen ? "rotate-180" : ""}`}
                        />
                      </button>

                      {isUserMenuOpen && (
                        <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-200 py-1 overflow-hidden">
                          {/* User info header */}
                          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                            <p className="text-sm font-medium text-gray-900">
                              {user?.firstName} {user?.lastName}
                            </p>
                            <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                          </div>

                          <Link
                            to="/dashboard/settings"
                            onClick={() => setIsUserMenuOpen(false)}
                            className="flex items-center space-x-3 px-4 py-3 hover:bg-gray-50 transition-colors"
                          >
                            <Settings size={18} className="text-gray-400" />
                            <span className="text-sm text-gray-700">Inställningar</span>
                          </Link>

                          <Link
                            to="/dashboard/billing"
                            onClick={() => setIsUserMenuOpen(false)}
                            className="flex items-center space-x-3 px-4 py-3 hover:bg-gray-50 transition-colors"
                          >
                            <CreditCard size={18} className="text-gray-400" />
                            <span className="text-sm text-gray-700">Betalningar</span>
                          </Link>

                          {(user?.hasAdminAccess || user?.role === "super_admin") && (
                            <Link
                              to="/admin"
                              onClick={() => setIsUserMenuOpen(false)}
                              className="flex items-center space-x-3 px-4 py-3 hover:bg-gray-50 transition-colors"
                            >
                              <Shield size={18} className="text-amber-600" />
                              <span className="text-sm text-gray-700">Admin-panel</span>
                            </Link>
                          )}

                          <div className="border-t border-gray-200 my-1" />

                          <button
                            onClick={handleLogout}
                            className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                          >
                            <LogOut size={18} className="text-red-600" />
                            <span className="text-sm text-red-600">Logga ut</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <button
                      onClick={openLogin}
                      className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors px-3 py-2"
                    >
                      Logga in
                    </button>
                    <button
                      onClick={openRegister}
                      className="btn btn-primary text-sm"
                    >
                      Kom igång gratis
                    </button>
                  </>
                )}
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                aria-expanded={isMobileMenuOpen}
                aria-label={isMobileMenuOpen ? "Stäng meny" : "Öppna meny"}
              >
                {isMobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>
        </nav>
      </header>

      {/* Mobile Menu - OUTSIDE header for proper fixed positioning */}
      <div
        className={`md:hidden fixed inset-0 z-40 transition-all duration-300 ${
          isMobileMenuOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/20 backdrop-blur-sm"
          onClick={() => setIsMobileMenuOpen(false)}
        />

        {/* Menu Panel */}
        <div
          className={`absolute top-16 left-0 right-0 bottom-0 bg-white overflow-y-auto transition-transform duration-300 ${
            isMobileMenuOpen ? "translate-y-0" : "-translate-y-4"
          }`}
        >
          <div className="px-4 py-6 space-y-1">
            {/* Products Section */}
            <div className="pb-2">
              <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Produkter
              </div>
              {NAV_LINKS.products.items.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className="flex items-center gap-3 px-3 py-3 text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-xl transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <div className={`p-2 rounded-lg ${item.bgColor}`}>
                    <item.icon className={`w-5 h-5 ${item.color}`} />
                  </div>
                  <div>
                    <div className="font-semibold">{item.label}</div>
                    <div className="text-xs text-gray-500">{item.description}</div>
                  </div>
                </Link>
              ))}
            </div>

            <div className="border-t border-gray-100 my-4" />

            {/* Main Links */}
            {NAV_LINKS.main.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="block px-3 py-3 text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-xl transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}

            <div className="border-t border-gray-100 my-4" />

            {/* Auth Buttons - Mobile */}
            <div className="px-3 space-y-2 pt-2">
              {isAuthenticated ? (
                <>
                  {/* User info */}
                  <div className="flex items-center gap-3 px-3 py-3 bg-gray-50 rounded-xl mb-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <User size={20} className="text-blue-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {user?.firstName} {user?.lastName}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                    </div>
                  </div>

                  <Link
                    to="/dashboard"
                    className="flex items-center gap-3 px-4 py-3 text-base font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <LayoutDashboard size={20} />
                    Dashboard
                  </Link>

                  <Link
                    to="/dashboard/settings"
                    className="flex items-center gap-3 px-4 py-3 text-base font-medium text-gray-700 hover:bg-gray-50 rounded-xl transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Settings size={20} className="text-gray-400" />
                    Inställningar
                  </Link>

                  <Link
                    to="/dashboard/billing"
                    className="flex items-center gap-3 px-4 py-3 text-base font-medium text-gray-700 hover:bg-gray-50 rounded-xl transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <CreditCard size={20} className="text-gray-400" />
                    Betalningar
                  </Link>

                  {(user?.hasAdminAccess || user?.role === "super_admin") && (
                    <Link
                      to="/admin"
                      className="flex items-center gap-3 px-4 py-3 text-base font-medium text-gray-700 hover:bg-gray-50 rounded-xl transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <Shield size={20} className="text-amber-600" />
                      Admin-panel
                    </Link>
                  )}

                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-3 w-full px-4 py-3 text-base font-medium text-red-600 hover:bg-red-50 rounded-xl transition-colors text-left"
                  >
                    <LogOut size={20} />
                    Logga ut
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={openRegister}
                    className="block w-full text-center px-4 py-3 text-base font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg shadow-blue-500/25"
                  >
                    Kom igång gratis
                  </button>
                  <button
                    onClick={openLogin}
                    className="block w-full text-center px-4 py-3 text-base font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
                  >
                    Logga in
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        currentView={authModalView}
        onViewChange={setAuthModalView}
      />
    </>
  );
}
